import React from "react";

import {
  MyTeamsSection,
  ModuleMenu,
  TeamsNavSection,
  type ModuleMenuOption,
  type ModuleMenuSection,
} from "#app/components/navigation/module-menu";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useNavDialogActions } from "#app/lib/nav-dialogs-context";

export function TeamsModuleMenu() {
  const { teams } = useWorkspaceData();
  const canCreateTeam = usePermission("manage.teams");
  // Same grant-driven gate as the rail's Invite tile and the Members page.
  const canInvite = useCanInvite();
  const closeSidebar = useCloseSidebarIfMobile();
  const { openCreateTeam, openInviteMembers } = useNavDialogActions();

  // Order/inclusion here is the instance default; user preferences will reorder/
  // filter by section id. The Teams section lists all accessible teams (decision
  // #4), skipped only when there's nothing to show or do (no teams AND no create
  // permission).
  const sections = React.useMemo<ModuleMenuSection[]>(() => {
    const list: ModuleMenuSection[] = [
      { id: "nav", Component: TeamsNavSection },
    ];
    if (teams.length > 0 || canCreateTeam) {
      list.push({ id: "my-teams", Component: MyTeamsSection });
    }
    return list;
  }, [teams.length, canCreateTeam]);

  const options = React.useMemo<ModuleMenuOption[]>(
    () => [
      ...(canCreateTeam
        ? [
            {
              id: "create-team",
              // Matches TeamsNavSection's "All Teams" icon.
              icon: "users" as const,
              label: "Create Team",
              onClick: () => {
                closeSidebar();
                openCreateTeam();
              },
            },
          ]
        : []),
      ...(canInvite
        ? [
            {
              id: "invite-member",
              icon: "user-plus" as const,
              label: "Invite Members",
              onClick: () => {
                closeSidebar();
                openInviteMembers();
              },
            },
          ]
        : []),
    ],
    [canCreateTeam, canInvite, closeSidebar, openCreateTeam, openInviteMembers],
  );

  return (
    <>
      <ModuleMenu title="Teams" sections={sections} options={options} />
    </>
  );
}
